/**
 * Dev mode (/dev): play a private copy of the garden with the Thicket RPG Lab's
 * art, skip days, and set up any scene to fine-tune how things look in play.
 * Nothing here reaches the real garden.
 */
import {
  ANIMAL_KINDS, BIOMES, DIFFICULTIES, findOpenSpot, GROWTH_STAGES, localDay, STARTER_BIOMES,
  type Biome, type Difficulty, type GameState, type Plant, type StarterBiome,
} from '@thicket/core';
import { html, nothing } from 'lit-html';
import { art, useArt } from '../garden/art/index.js';
import { labArt, labLook, newVisit, setSceneryTrees } from '../garden/art/lab/index.js';
import { placeholderArt } from '../garden/art/placeholder/index.js';
import type { SandboxStore } from '../dev/sandbox.js';
import { app, BIOME_NAMES, toast, update } from './app.js';

const sandbox = () => app.store as SandboxStore;
const biomeOf = (s: GameState): Biome => app.biome ?? s.profile.starterBiome;
const TEST_NAMES: Record<Difficulty, [string, string, string]> = {
  trivial: ['Make the bed', 'Drink water', 'Reply to a text'],
  easy: ['Anki reviews', 'Stretch', 'Return library books'],
  medium: ['Study block', 'Walk outside', 'Outline the paper'],
  hard: ['Gym', 'Deep work', 'Finish the essay'],
};

/** The strip at the top of every screen in dev mode. */
export function devBar() {
  if (!app.dev) return nothing;
  const sb = sandbox();
  const s = sb.view;
  const day = s ? localDay(sb.now(), s.profile) : '';
  return html`<div class="devbar" role="note">
    <span><b>Dev mode</b><span class="devbar-note"> · a sandbox copy; nothing here changes your real garden</span>${sb.offsetDays ? html` · <b>${day}</b> (+${sb.offsetDays} days)` : nothing}</span>
    <a class="devbar-exit" href="/">Exit</a>
  </div>`;
}

function placeEverything(biome: Biome) {
  const sb = sandbox();
  const s = sb.view!;
  const live = new Set(s.tasks.filter((t) => !t.archivedAt).map((t) => t.id));
  let placed = 0;
  for (const p of s.plants) {
    if (p.goneAt || !p.taskId || !live.has(p.taskId)) continue;
    if ((p.kind === 'tree' || p.kind === 'wildflower') && p.biome === null) {
      const spot = findOpenSpot(sb.view!, biome, 'anywhere', p.id, p);
      if (spot && sb.act({ type: 'placePlant', plantId: p.id, spot: { where: 'ground', biome, ...spot } })) placed++;
    }
  }
  const taken = new Set(sb.view!.plants.filter((p) => p.kind === 'bed' && !p.goneAt && p.plot !== null).map((p) => p.plot));
  let plot = 0;
  for (const p of sb.view!.plants) {
    if (p.kind !== 'bed' || p.goneAt || p.plot !== null) continue;
    while (taken.has(plot)) plot++;
    if (plot >= sb.rules.bedPlots) break;
    if (sb.act({ type: 'placePlant', plantId: p.id, spot: { where: 'bed', plot } })) {
      taken.add(plot);
      placed++;
    }
  }
  return placed;
}

function testGarden(biome: Biome) {
  const sb = sandbox();
  for (const d of DIFFICULTIES) {
    const [daily, habit, todo] = TEST_NAMES[d];
    sb.act({ type: 'createTask', task: { type: 'daily', text: daily, difficulty: d, label: daily.split(' ')[0]!.slice(0, 10) } });
    sb.act({ type: 'createTask', task: { type: 'habit', text: habit, difficulty: d, up: true, down: true } });
    sb.act({ type: 'createTask', task: { type: 'todo', text: todo, difficulty: d } });
  }
  toast(`Added 12 test tasks and planted ${placeEverything(biome)} plants`);
}

function everyAnimal(s: GameState, biome: Biome) {
  const sb = sandbox();
  sb.cheat((x) => (x.profile.coins += 1000));
  let n = 0;
  for (const kind of ANIMAL_KINDS) {
    const needsTree = sb.rules.animals[kind].minTreeStage !== null;
    const ok = sb.act(needsTree ? { type: 'buyAnimal', kind } : { type: 'buyAnimal', kind, biome });
    if (ok) n++;
  }
  toast(n ? `${n} companions joined` : 'Grow some trees first: songbirds, squirrels and owls need one');
  void s;
}

function growAll(biome: Biome, step: number) {
  const sb = sandbox();
  const s = sb.view!;
  const plants = s.plants.filter((p) => !p.goneAt && (p.kind === 'bed' || (p.kind === 'tree' && p.biome === biome)));
  for (const p of plants) {
    const i = Math.max(0, Math.min(GROWTH_STAGES.length - 1, GROWTH_STAGES.indexOf(p.stage) + step));
    sb.setStage(p.id, GROWTH_STAGES[i]!);
  }
}

function setLeaves(biome: Biome, leaves: 'full' | 'sparse') {
  sandbox().cheat((s) => s.plants.forEach((p) => {
    if (p.kind === 'tree' && !p.goneAt && p.biome === biome) {
      p.leaves = leaves;
      if (leaves === 'full') p.streak = Math.max(p.streak, 1);
    }
  }));
}

function setBud(plantId: string, how: 'bud' | 'overdue' | 'bloom' | 'last') {
  const sb = sandbox();
  const yesterday = localDay(new Date(sb.now().getTime() - 86_400_000), sb.view!.profile);
  sb.cheat((s) => {
    const p = s.plants.find((x) => x.id === plantId);
    if (!p) return;
    const task = s.tasks.find((t) => t.id === p.taskId);
    p.bloomed = how === 'bloom' || how === 'last';
    p.bloomDaysLeft = how === 'last' ? 1 : p.bloomed ? sb.rules.bloomDays[p.pace] : null;
    if (task && task.type === 'todo') {
      task.completed = p.bloomed;
      task.dueDate = how === 'overdue' ? yesterday : how === 'bud' ? null : task.dueDate;
    }
  });
}

function describe(p: Plant, s: GameState) {
  const task = s.tasks.find((t) => t.id === p.taskId);
  const name = task?.text ?? (p.kind === 'flower' ? `${p.species} (seed packet)` : p.kind);
  return `${name} · ${p.kind === 'wildflower' ? (p.bloomed ? 'bloom' : 'bud') : p.stage} · ${p.pace}`;
}

function picked(s: GameState) {
  const p = app.devPick ? s.plants.find((x) => x.id === app.devPick && !x.goneAt) : null;
  if (!p) return html`<p class="small muted" style="margin:0">Tap a plant in the scene (or a bed plot below) to tune it here.</p>`;
  const sb = sandbox();
  const stageRow = (p.kind === 'tree' || p.kind === 'bed') ? html`<div class="chips">${GROWTH_STAGES.map((st) => html`
    <button class="chip" aria-pressed=${p.stage === st} @click=${() => sb.setStage(p.id, st)}>${st}</button>`)}</div>` : nothing;
  const leafRow = p.kind === 'tree' ? html`<div class="chips">${(['full', 'sparse'] as const).map((l) => html`
    <button class="chip" aria-pressed=${p.leaves === l} @click=${() => sb.cheat((x) => { const q = x.plants.find((y) => y.id === p.id); if (q) q.leaves = l; })}>${l} leaves</button>`)}</div>` : nothing;
  const budRow = p.kind === 'wildflower' ? html`<div class="chips">${([['bud', 'Bud'], ['overdue', 'Overdue bud'], ['bloom', 'Bloom'], ['last', 'Last day (going to seed)']] as const).map(([k, l]) => html`
    <button class="chip" @click=${() => setBud(p.id, k)}>${l}</button>`)}</div>` : nothing;
  return html`<div class="devpick">
    <div class="section-head" style="margin:0"><b>${describe(p, s)}</b>
      <button class="link" @click=${() => update(() => (app.devPick = null))}>Done</button></div>
    ${stageRow}${leafRow}${budRow}
    ${p.kind === 'wildflower' && p.bloomed ? html`<div class="chips"><button class="chip" @click=${() => update(() => (app.sheet = { kind: 'bloom', plantId: p.id }))}>Pick it…</button></div>` : nothing}
    <div class="chips"><button class="chip danger" @click=${() => { sb.cheat((x) => { const q = x.plants.find((y) => y.id === p.id); if (q) { q.goneAt = sb.now().toISOString(); q.goneReason = 'pulled'; } }); app.devPick = null; }}>Remove it</button></div>
  </div>`;
}

/** Dev controls under the garden scene. */
export function devPanel(s: GameState) {
  if (!app.dev) return nothing;
  const sb = sandbox();
  const biome = biomeOf(s);
  const unlocked = new Set(s.unlocks.map((u) => u.biome));
  const usingLab = art === labArt;
  return html`<section class="box devpanel">
    <div class="section-head"><h3>Dev mode</h3><span class="muted small">${BIOME_NAMES[biome]} · ${usingLab ? 'Lab art' : 'placeholder art'}</span></div>

    <div class="devgroup"><span class="devlab">Selected</span>${picked(s)}</div>

    <div class="devgroup"><span class="devlab">Look</span><div class="chips">
      <button class="chip" aria-pressed=${labLook.dusk} @click=${() => update(() => (labLook.dusk = !labLook.dusk))}>Dusk</button>
      <button class="chip" @click=${() => update(newVisit)}>New visitors</button>
      <button class="chip" aria-pressed=${labLook.trees} title="Starting scenes have no trees of their own; this puts the Lab's scenery trees back to compare"
        @click=${() => update(() => setSceneryTrees(!labLook.trees))}>Scenery trees</button>
      <button class="chip" aria-pressed=${!usingLab} @click=${() => update(() => useArt(usingLab ? placeholderArt : labArt))}>Placeholder art</button>
    </div></div>

    <div class="devgroup"><span class="devlab">Time</span><div class="chips">
      <button class="chip" @click=${() => sb.skipDays(1)}>Next day</button>
      <button class="chip" @click=${() => sb.skipDays(7)}>+7 days</button>
    </div></div>

    <div class="devgroup"><span class="devlab">Garden</span><div class="chips">
      <button class="chip" @click=${() => testGarden(biome)}>Plant a test garden</button>
      <button class="chip" @click=${() => toast(`Planted ${placeEverything(biome)}`)}>Plant everything waiting</button>
      <button class="chip" @click=${() => growAll(biome, 1)}>Grow all +1</button>
      <button class="chip" @click=${() => growAll(biome, -1)}>Shrink all −1</button>
      <button class="chip" @click=${() => setLeaves(biome, 'sparse')}>All leaves sparse</button>
      <button class="chip" @click=${() => setLeaves(biome, 'full')}>All leaves full</button>
      <button class="chip" @click=${() => everyAnimal(s, biome)}>One of every companion</button>
    </div></div>

    <div class="devgroup"><span class="devlab">Player</span><div class="chips">
      <button class="chip" @click=${() => sb.cheat((x) => (x.profile.coins += 100))}>+100 coins</button>
      <button class="chip" @click=${() => sb.cheat((x) => (x.profile.xp += 500))}>+500 XP</button>
      <button class="chip" @click=${() => sb.cheat((x) => {
        for (const b of BIOMES) if (!x.unlocks.some((u) => u.biome === b)) x.unlocks.push({ biome: b, unlockedAt: sb.now().toISOString(), reason: 'level' });
      })}>Unlock every biome</button>
    </div></div>

    <div class="devgroup"><span class="devlab">Biome</span><div class="chips">${BIOMES.map((b) => html`
      <button class="chip" aria-pressed=${b === biome} @click=${() => update(() => {
        if (!unlocked.has(b)) sb.cheat((x) => x.unlocks.push({ biome: b, unlockedAt: sb.now().toISOString(), reason: 'level' }));
        app.biome = b;
        app.devPick = null;
      })}>${BIOME_NAMES[b]}</button>`)}</div></div>

    <div class="devgroup"><span class="devlab">Start over</span><div class="chips">
      <button class="chip" @click=${() => void sb.copyReal().then(() => update(() => { app.biome = null; app.devPick = null; toast('Copied your real garden'); }))}>Copy my real garden</button>
      ${STARTER_BIOMES.map((b: StarterBiome) => html`<button class="chip" @click=${() => update(() => { sb.fresh(b); app.biome = null; app.devPick = null; })}>New player: ${BIOME_NAMES[b]}</button>`)}
    </div></div>
  </section>`;
}
